// @ts-check

/**
 * @typedef {Object} IntersectionObserverOptions
 * @property {number} threshold
 * @property {Element|null} [root]
 * @property {string} [rootMargin]
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Intersection_observer_options
 */

class ViewportObserver {
  /**
   * @param {Object} options
   * @param {IntersectionObserverOptions} options.intersectionObserverOptions
   * @param {(element: Element, extra: any) => void} options.callback
   */
  constructor(options) {
    this.callback = options.callback
    // Bind event handlers to class instance
    this.onElementVisible = this.onElementVisible.bind(this)
    this.onIntersectionChange = this.onIntersectionChange.bind(this)
    // Initialize instance state
    /** @type {WeakMap<Element, any>} */
    this.elements = new WeakMap()
    // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Intersection_observer_options
    this.observer = new IntersectionObserver(
      this.onIntersectionChange,
      options.intersectionObserverOptions
    )
  }

  // API

  /**
   * @param {Element} element
   * @param {any} extra
   */
  observe(element, extra) {
    this.elements.set(element, extra)
    this.observer.observe(element)
  }

  /**
   * @param {Element} element
   */
  unobserve(element) {
    this.observer.unobserve(element)
    this.elements.delete(element)
  }

  // Event handlers

  /**
   * @param {Array.<IntersectionObserverEntry>} entries
   */
  onIntersectionChange(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.onElementVisible(entry.target)
      }
    })
  }

  /**
   * @param {Element} element
   */
  onElementVisible(element) {
    if (this.elements.has(element)) {
      const extra = this.elements.get(element)
      this.callback(element, extra)
    }
    this.unobserve(element)
  }
}

module.exports = ViewportObserver
